import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout from '../components/LegalLayout';
import { SUPPORT_EMAIL } from '../config/support';
import usePageMeta from '../hooks/usePageMeta';

// ---------------------------------------------------------------------------
// NOT LEGAL ADVICE. These terms exist so the product has publicly reachable
// terms of service, which both Google Play and the Microsoft Store require
// (audit B4 / Finding 5.4a).
//
// They have NOT been reviewed by an attorney. The limitation of liability,
// warranty disclaimer and indemnity clauses in particular are ordinary SaaS
// boilerplate written in plain English, not tailored language, and their
// enforceability has not been checked against Wisconsin law. Have a lawyer
// read this before relying on it in a dispute or signing a customer with real
// contractual leverage.
//
// Keep it truthful: describe the billing process that actually exists. That is
// now self-serve Stripe Checkout for the subscription, plus a Stripe billing
// portal for card changes and cancellation, and it is separate from the Square
// invoices a contractor sends their OWN customers. This comment used to say
// there was no self-serve checkout, which stopped being true when Stripe
// Checkout shipped. Update it in the same commit as any change it describes.
// ---------------------------------------------------------------------------

function Terms() {
  usePageMeta({
    title: 'Terms of Service | SnowTrack',
    description: 'The SnowTrack terms of service in plain English: your subscription, billing through Stripe, cancellation, and what happens to your data.',
    path: '/terms',
  });

  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The agreement between you and SnowTrack™. Plain English, no tricks."
      updated="October 22, 2026"
    >
      <p>
        <strong>SnowTrack&trade; is operated by CW Affiliate Investments, LLC.</strong>
      </p>

      <div className="legal-callout">
        <p>
          <strong>The short version.</strong> Use SnowTrack™ to run your business, keep your login
          secure, pay your invoices, and do not do anything illegal with it. Your data stays
          yours and you can take it with you or delete it at any time. We will do our best to
          keep the service up but we cannot promise perfection, and our liability is limited to
          what you have paid us.
        </p>
      </div>

      <h2>1. Agreement to these terms</h2>
      <p>
        By creating an account or using SnowTrack™ you agree to these terms. If you are agreeing
        on behalf of a company, you confirm you have the authority to bind that company. If you
        do not agree, do not use the service.
      </p>
      <p>
        These terms work together with our <Link to="/privacy">Privacy Policy</Link>, which
        explains what data we handle and why.
      </p>

      <h2>2. What the service is</h2>
      <p>
        SnowTrack™ is a hosted, subscription-based business management platform for snow removal
        and related property service contractors. It provides customer and property records,
        route building and optimization, a storm-day operations mode called Storm Mode, service
        history, photo records, expense tracking, profit and loss reporting, bulk customer email,
        customer text messaging (SMS), a customer portal, team accounts, email notifications, and invoicing sent from your own
        connected Square account.
      </p>
      <p>
        We may add, change or retire features. Where a change removes something you rely on, we
        will give you reasonable notice. Features described as &quot;coming soon&quot; are not
        part of what you are buying and are not promised on any date.
      </p>

      <h2>3. Your account</h2>
      <ul>
        <li>Give us accurate registration information and keep it current.</li>
        <li>
          Keep your password secret. You are responsible for everything done under your login and
          under the logins of team members you invite.
        </li>
        <li>
          Tell us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> as soon as you
          suspect unauthorised access.
        </li>
        <li>
          You must be at least 18 and legally able to enter a contract. SnowTrack™ is for business
          use, not personal use.
        </li>
        <li>
          The company owner controls the account, including who has access and whether the
          account is deleted.
        </li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the service for anything illegal, fraudulent or deceptive.</li>
        <li>
          Upload data you have no right to hold, or contact people who have asked you not to.
          When you enter customer records you confirm you are entitled to hold and process them.
        </li>
        <li>
          Send unsolicited bulk email through the service. Email only your own customers. Every
          bulk email carries an unsubscribe link and your business name and postal address, which
          you must keep current in Settings. When a customer unsubscribes, SnowTrack stops your
          bulk email to them; do not turn their email back on unless they ask you to.
        </li>
        <li>
          Send text messages to anyone who has not opted in through the service, or after they
          have replied STOP. Text messages must be about the services you provide to that
          customer.
        </li>
        <li>
          Upload malicious code, or attempt to access another company&apos;s data, probe our
          systems, defeat rate limits, or interfere with the service for anyone else.
        </li>
        <li>
          Resell, sublicense or white-label the service, or scrape it to build a competing
          product.
        </li>
        <li>Upload photos or content that is unlawful, or that you had no right to capture.</li>
      </ul>
      <p>
        We may suspend or terminate an account that breaches this section, without notice where
        the breach is serious or ongoing.
      </p>

      <h2>5. Payment terms</h2>
      <ul>
        <li>
          <strong>Trial.</strong> New accounts get a free trial including one complete storm
          event. No card is required and nothing is charged automatically.
        </li>
        <li>
          <strong>Plans and prices.</strong> Plans are billed monthly at the price shown on our{' '}
          <Link to="/pricing">pricing page</Link>. There are no usage charges or per-storm fees.
        </li>
        <li>
          <strong>What a plan includes.</strong> Each plan includes a set number of user accounts
          and a set number of customers, listed on the pricing page. Reaching a limit stops you
          adding more until you upgrade. It never removes or hides anything you already have, and
          it never stops you running routes, completing stops or sending invoices. Properties per
          customer and storm events are unlimited on every plan.
        </li>
        <li>
          <strong>How billing happens.</strong> You choose a plan and pay by card through{' '}
          <strong>Stripe Checkout</strong>. Card details go to Stripe and never reach us. You can
          change your card, see your invoices, or cancel from the billing portal in Settings,
          which is also Stripe. If you would rather be invoiced directly, email{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we will arrange it.
        </li>
        <li>
          <strong>Late payment.</strong> If an invoice goes unpaid we may suspend access after
          giving you notice and a chance to pay. Your data is not deleted while suspended.
        </li>
        <li>
          <strong>Price changes.</strong> We may change prices, but not retroactively and not
          without at least 30 days&apos; notice to the email address on your account. If you do not
          accept a new price you may cancel before it takes effect.
        </li>
        <li>
          <strong>Refunds.</strong> Plans are month-to-month. We do not generally refund a month
          already begun, but if something went wrong on our side, ask - we would rather fix it
          than argue.
        </li>
        <li>
          <strong>Taxes.</strong> Prices exclude any applicable sales or use tax, which is your
          responsibility where it applies.
        </li>
      </ul>
      <p>
        Nothing in this section relates to the invoices <em>you</em> issue to your own customers.
        Those are entirely between you and them; we only provide the tooling.
      </p>

      <h2>6. Your data</h2>
      <ul>
        <li>
          <strong>You own it.</strong> Your account data, customer records, service history,
          photos and financial records remain yours. We claim no ownership.
        </li>
        <li>
          <strong>Your Square account is yours.</strong> Connecting Square authorises us to
          create invoices on your behalf. It gives us no other access, it does not let us move
          money, and you can revoke it from Settings at any time. Invoices you issue to your
          customers are between you and them.
        </li>
        <li>
          <strong>We host it.</strong> You grant us the limited licence needed to store, process,
          transmit and display your data in order to provide the service - including sending it
          to the third-party processors named in our <Link to="/privacy">Privacy Policy</Link>.
        </li>
        <li>
          <strong>You are responsible for it.</strong> Accuracy, legality and the right to hold
          it are yours. If one of your customers asks you to correct or delete their record, that
          is your obligation to meet; the app gives you the tools.
        </li>
        <li>
          <strong>Export.</strong> You can export your data from the app while your account is
          active. Ask us if you need a fuller export.
        </li>
      </ul>

      <h2>7. Service availability</h2>
      <p>
        We work to keep SnowTrack™ available and fast, and we know a storm day is the worst
        possible time for an outage. But the service is provided on a{' '}
        <strong>best-effort basis with no uptime guarantee</strong>. There is no service level
        agreement on Basic, Pro or Premium plans. Enterprise customers can agree specific
        availability and support commitments with us in writing; absent such a written agreement,
        this section applies.
      </p>
      <p>
        We may take the service down for maintenance, and will try to schedule it outside storm
        conditions and to warn you first. We are not responsible for outages caused by things
        outside our control, including failures at Railway, Netlify, Stripe, Square, SendGrid,
        Cloudinary, Google Maps or the National Weather Service, or by your own internet or
        device.
      </p>
      <p>
        <strong>Do not rely on the app as your only record of a storm.</strong> Keep whatever
        independent notes your business needs.
      </p>

      <h2>8. Third-party services</h2>
      <p>
        SnowTrack™ integrates with Stripe for your subscription payments, with Square for the
        invoices you send your own customers, and with SendGrid, Cloudinary, Google Maps
        Platform, OpenStreetMap and the National Weather Service. Your use of those services
        through SnowTrack™ is also subject to their terms. We are not responsible for their acts,
        omissions, pricing, outages or data practices, and weather data in particular is supplied
        by third parties and may be wrong or late.
      </p>

      <h2>9. Text messaging</h2>
      <p>
        Text messages are delivered through Twilio. Message frequency varies and message and data
        rates may apply to the recipient. Recipients can reply STOP to opt out and HELP for help
        at any time. You are responsible for obtaining and keeping consent from your own
        customers; the service records the consent it captures but you remain the sender of
        record.
      </p>

      <h2>10. Warranty disclaimer</h2>
      <p>
        The service is provided <strong>&quot;as is&quot; and &quot;as available&quot;</strong>,
        without warranties of any kind, whether express or implied, including any implied
        warranties of merchantability, fitness for a particular purpose, or non-infringement. We
        do not warrant that the service will be uninterrupted or error-free, that route
        optimization will produce an optimal route, or that weather data will be accurate.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, neither SnowTrack™ nor anyone working with us is
        liable for indirect, incidental, special, consequential, exemplary or punitive damages, or
        for lost profits, lost revenue, lost business, lost data, or the cost of substitute
        services, however caused and on any theory of liability.
      </p>
      <p>
        Our total aggregate liability arising out of or relating to the service is limited to{' '}
        <strong>
          the amount you actually paid us in the twelve months before the event giving rise to the
          claim
        </strong>
        . If you have paid us nothing, our total liability is limited to one hundred United States
        dollars (USD 100).
      </p>
      <p>
        Some jurisdictions do not allow the exclusion of certain warranties or the limitation of
        certain damages. Where that applies to you, the exclusions and limits above apply only to
        the extent permitted, and nothing here limits liability for fraud, gross negligence or
        anything else that cannot lawfully be limited.
      </p>

      <h2>12. Indemnity</h2>
      <p>
        You agree to indemnify and hold us harmless from claims, damages and reasonable costs
        arising out of your use of the service in breach of these terms, your data or content, or
        a dispute between you and one of your own customers.
      </p>

      <h2>13. Termination</h2>
      <ul>
        <li>
          <strong>By you.</strong> Cancel at any time by emailing{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. There is no cancellation fee
          and no notice period. A company owner can also delete the account outright from{' '}
          <strong>Settings &rarr; Delete Account</strong>, or request deletion without logging in
          from the <Link to="/delete-account">account deletion request page</Link>.
        </li>
        <li>
          <strong>By us.</strong> We may terminate or suspend your account for a breach of these
          terms, for non-payment after notice, or if we stop offering the service. Except where
          the breach is serious, we will give you reasonable notice first.
        </li>
        <li>
          <strong>Data after termination.</strong> Unless you asked us to delete it immediately,
          your data remains available for export for <strong>30 days</strong> after termination.
          After that it is deleted from the live database, and rolls out of encrypted backups
          within a further 30 days.
        </li>
        <li>
          <strong>Deletion you initiate is immediate.</strong> Deleting your account from Settings
          removes your company, users, customers, properties, routes, services, invoices and
          expenses straight away. It cannot be undone and there is no 30-day export window.
          Export first if you want a copy.
        </li>
      </ul>

      <h2>14. Changes to these terms</h2>
      <p>
        We may update these terms. For a material change we will email the address on your
        account at least 30 days before it takes effect, and update the date at the top of this
        page. Continuing to use the service after that date means you accept the new terms; if you
        do not, cancel before it takes effect.
      </p>

      <h2>15. Governing law</h2>
      <p>
        These terms are governed by the laws of the <strong>State of Wisconsin</strong>, United
        States, without regard to its conflict of law rules. Any dispute arising out of or
        relating to these terms or the service will be brought exclusively in the state or
        federal courts located in Wisconsin, and you and we each consent to the jurisdiction of
        those courts.
      </p>

      <h2>16. General</h2>
      <ul>
        <li>
          If any provision of these terms is held unenforceable, the rest remains in force.
        </li>
        <li>
          Our failure to enforce a provision is not a waiver of it.
        </li>
        <li>
          You may not assign these terms without our consent. We may assign them in connection
          with a merger, acquisition or sale of the business.
        </li>
        <li>
          These terms, together with the Privacy Policy, are the entire agreement between us,
          except where we have signed a separate written agreement with you - in which case that
          agreement controls where it conflicts.
        </li>
        <li>
          Nothing here creates a partnership, joint venture, employment or agency relationship.
        </li>
      </ul>

      <h2>17. Contact</h2>
      <p>
        Questions about these terms go to{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
      </p>
    </LegalLayout>
  );
}

export default Terms;
